import {isValidElement, ReactNode} from 'react';

import {MessageDescriptor, useIntl} from 'react-intl';

import {view, tokens, createView} from '@sail/ui';

import {useTheme} from '../ThemeProvider';
import {AISparkle} from './AISparkle';

type Props = {
  title?: MessageDescriptor | ReactNode;
  height?: number | string;
  badge?: ReactNode;
  headerActions?: ReactNode;
  subHeader?: ReactNode;
  hideSparkle?: boolean;
};

function isMessageDescriptor(value: unknown): value is MessageDescriptor {
  return (
    value != null &&
    typeof value === 'object' &&
    !isValidElement(value) &&
    'id' in value
  );
}

function resolveTitle(
  title: MessageDescriptor | ReactNode,
  intlFormatMessage: ReturnType<typeof useIntl>['formatMessage'],
  fallback: string | undefined,
): ReactNode {
  if (title == null) return fallback;
  if (isMessageDescriptor(title)) return intlFormatMessage(title);
  return title;
}

export const AssistantHeader = createView(
  ({
    title,
    height = 52,
    badge,
    headerActions,
    subHeader,
    hideSparkle = false,
    ...props
  }: Props) => {
    const intl = useIntl();
    const theme = useTheme();
    const resolvedTitle = resolveTitle(
      title,
      intl.formatMessage,
      theme.assistantHeaderTitle,
    );
    const titleNode = isValidElement(resolvedTitle) ? (
      resolvedTitle
    ) : (
      <view.span
        css={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {resolvedTitle}
      </view.span>
    );

    return (
      <view.header
        data-testid="assistant-header"
        css={{boxShadow: 'medium'}}
        {...props}
      >
        <view.div
          css={{
            stack: 'x',
            paddingX: 'medium',
            paddingY: 'small',
            borderBottom: 'var(--assistant-header-border, 1px solid)',
            borderBottomColor: 'border',
            alignY: 'center',
            distribute: 'space-between',
            zIndex: 2,
            height,
            background: `color-mix(in rgb, ${tokens.color.background.surface} 25%, transparent 75%)`,
            backdropFilter: 'blur(4px)',
          }}
        >
          <view.h1
            css={{
              font: 'heading.small',
              margin: 'space.0',
              stack: 'x',
              gap: 'small',
              alignY: 'center',
              minWidth: 0,
            }}
          >
            {hideSparkle ? null : <AISparkle />}
            {titleNode}
            {badge}
          </view.h1>

          <view.div css={{bleedRight: 'small', gap: 'xsmall', flexShrink: 0}}>
            {headerActions}
          </view.div>
        </view.div>

        {subHeader}
      </view.header>
    );
  },
);
